import { useNavigate, useLocation } from "react-router-dom";
import { History, Settings, LogOut, LogIn, Crown, Users, Coins, Bookmark, Clapperboard, Video, Smile, Hammer, Lock, Wallet as WalletIcon, Mail, Heart, Store, BarChart3, Mic, HelpCircle } from "lucide-react";
import ContactModal from "@/components/ContactModal";
import { useAuth } from "@/context/AuthContext";
import { useState, useRef, useEffect } from "react";
import AvatarModal from "@/components/AvatarModal";
import { AdminCostsModal } from "@/components/AdminCostsModal";
import api from "@/lib/api";

export default function Header({ onOpenSettings }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const createInPage = ["/", "/my-videos", "/wallet", "/buy-later", "/pricing", "/diy", "/minute", "/studio"].includes(pathname) || pathname.startsWith("/minute/");
  const { user, logout, savedCount } = useAuth();
  const [menu, setMenu] = useState(false);
  const [showAvatar, setShowAvatar] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showCosts, setShowCosts] = useState(false);
  const [diyOn, setDiyOn] = useState(false);
  const ref = useRef(null);
  const bellRef = useRef(null);
  const [bellOpen, setBellOpen] = useState(false);
  const [loves, setLoves] = useState([]);
  const [unseenLoves, setUnseenLoves] = useState(0);
  const [loves24, setLoves24] = useState(0);
  const [loves7, setLoves7] = useState(0);
  const [loves30, setLoves30] = useState(0);
  const [lovePeriod, setLovePeriod] = useState("1d");

  useEffect(() => {
    if (!user) { setUnseenLoves(0); setLoves24(0); return; }
    let alive = true;
    const poll = () => api.get("/me/loves/unseen-count").then(({ data }) => { if (alive) { setUnseenLoves(data.unseen || 0); setLoves24(data.count_24h || 0); setLoves7(data.count_7d || 0); setLoves30(data.count_30d || 0); } }).catch(() => {});
    poll();
    const t = setInterval(poll, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [user?.user_id]);

  const openBell = () => {
    const willOpen = !bellOpen;
    setBellOpen(willOpen);
    if (willOpen) {
      api.get("/me/loves").then(({ data }) => setLoves(data.loves || [])).catch(() => setLoves([]));
      if (unseenLoves > 0) api.post("/me/loves/seen").then(() => setUnseenLoves(0)).catch(() => {});
    }
  };

  useEffect(() => {
    const h = (e) => bellRef.current && !bellRef.current.contains(e.target) && setBellOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/config").then(({ data }) => setDiyOn(data.diy_available !== false)).catch(() => {});
  }, [user]);

  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setMenu(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const initial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  return (
    <header className="sms-header-dark w-full border-b border-[#26262e] bg-black backdrop-blur-md sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 h-16 flex items-center justify-between">
        <button
          data-testid="header-logo-btn"
          onClick={() => navigate("/")}
          className="flex items-center gap-1.5 sm:gap-2 group shrink-0"
        >
          <img src="/vittyyo-wordmark.svg?v=4" alt="vittyyo" data-testid="header-wordmark" className="h-8 sm:h-10 w-auto transition-transform group-hover:scale-[1.03]" draggable={false} />
        </button>

        <div className="flex flex-1 lg:flex-none items-center justify-between lg:justify-end gap-0.5 lg:gap-1 pl-1 lg:pl-0 pr-0 lg:pr-3">
          {!user?.unlimited ? (
            !user ? (
              <button
                data-testid="header-login-btn"
                onClick={() => navigate("/login")}
                className="flex items-center gap-1.5 text-sm font-bold text-white/80 hover:text-white px-3 py-2 rounded-full transition-colors"
              >
                <LogIn size={16} /> Log in
              </button>
            ) : (
              <>
              <button
                data-testid="header-new-video-btn"
                onClick={() => navigate("/diy")}
                title="Build your video free — you only pay when you export"
                className={`${createInPage ? "hidden sm:flex" : "flex"} items-center gap-1 px-4 py-2 ml-1.5 sm:ml-0 rounded-full bg-[#1e3aaa] hover:bg-[#16267a] text-sm font-bold transition-colors shadow-sm shrink-0`}
              >
                <span className="text-white whitespace-nowrap">Free Create</span>
              </button>
              <button
                data-testid="header-community-btn"
                onClick={() => navigate("/")}
                className="group flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-[#1e3aaa] transition-colors"
                title="Community"
              >
                <Users size={18} className="text-[#5d8a52] group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold text-white">Community</span>
              </button>
              <button
                data-testid="header-myvideos-btn"
                onClick={() => navigate("/my-videos", { state: { t: Date.now() } })}
                className="group flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-[#1e3aaa] transition-colors"
                title="My Videos"
              >
                <Video size={18} className="text-[#3f6238] group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold"><span className="text-[#8fbf82] group-hover:text-white transition-colors">My</span> <span className="text-white">Videos</span></span>
              </button>
              <button
                data-testid="header-buylater-btn"
                onClick={() => navigate("/buy-later")}
                className="group flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-[#1e3aaa] transition-colors"
                title="Saved"
              >
                <Bookmark size={18} className="text-[#5d8a52] group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold text-white">Saved</span>
              </button>
              <button
                data-testid="header-wallet-btn"
                onClick={() => navigate("/wallet")}
                className="group flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-[#1e3aaa] transition-colors"
                title="My Purchases"
              >
                <WalletIcon size={18} className="text-[#3f6238] group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold text-white">Purchases</span>
              </button>
              <button
                data-testid="header-pricing-btn"
                onClick={() => navigate("/pricing")}
                title="Plans & pricing"
                className="group flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-[#1e3aaa] text-[#5d8a52] transition-colors"
              >
                <Coins size={18} className="group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold text-white">Plans</span>
              </button>
              {onOpenSettings && (
                <button
                  data-testid="header-settings-btn"
                  onClick={onOpenSettings}
                  title="Settings & credits"
                  className="group flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-white/10 transition-colors"
                >
                  <Settings size={18} className="text-white group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold text-white">Settings</span>
                </button>
              )}
              <button
                data-testid="header-logout-btn"
                onClick={() => { logout(); navigate("/"); }}
                className="group flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-white/10 transition-colors"
                title="Log out"
              >
                <LogOut size={18} className="text-white group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold text-white">Log out</span>
              </button>
              </>
            )
          ) : (
            <>
          <button
            data-testid="header-new-video-btn"
            onClick={() => navigate("/diy")}
            title="Build your video free — you only pay when you export"
            className={`${createInPage ? "hidden sm:flex" : "flex"} items-center gap-1 px-4 py-2 ml-1.5 sm:ml-0 rounded-full bg-[#1e3aaa] hover:bg-[#16267a] text-sm font-bold transition-colors shadow-sm`}
          >
            <span className="text-white whitespace-nowrap">Free Create</span>
          </button>
          <button
            data-testid="header-community-btn"
            onClick={() => navigate("/")}
            className="group flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-[#1e3aaa] text-[#4a475c] transition-colors"
            title="Community"
          >
            <Users size={18} className="text-[#5d8a52] group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold text-white">Community</span>
          </button>
              <button
                data-testid="header-history-btn"
                onClick={() => navigate("/my-videos", { state: { t: Date.now() } })}
                className="group flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-[#1e3aaa] transition-colors"
                title="My Videos"
              >
                <Video size={18} className="text-[#3f6238] group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold"><span className="text-[#8fbf82] group-hover:text-white transition-colors">My</span> <span className="text-white">Videos</span></span>
              </button>
              <button
                data-testid="header-buylater-btn"
                onClick={() => navigate("/buy-later")}
                className="group relative flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-[#1e3aaa] transition-colors"
                title="Saved"
              >
                <Bookmark size={18} className="text-[#5d8a52] group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold text-white">Saved</span>
              </button>
              <button
                data-testid="header-wallet-btn"
                onClick={() => navigate("/wallet")}
                className="group flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-[#1e3aaa] transition-colors"
                title="My Purchases"
              >
                <WalletIcon size={18} className="text-[#3f6238] group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold text-white">Purchases</span>
              </button>
              <button
                data-testid="header-pricing-btn"
                onClick={() => navigate("/pricing")}
                title="Plans & pricing"
                className="group flex items-center gap-1.5 p-1 lg:px-3 rounded-full hover:bg-[#1e3aaa] text-[#5d8a52] transition-colors"
              >
                <Coins size={18} className="group-hover:text-white transition-colors" /> <span className="hidden lg:inline text-sm font-semibold text-white">Plans</span>
              </button>
              <div className="relative" ref={bellRef}>
                <button
                  data-testid="header-bell-btn"
                  onClick={() => setLovePeriod((p) => (p === "1d" ? "7d" : p === "7d" ? "30d" : "1d"))}
                  title="Love received — tap to switch 1D / 7D / 30D"
                  className="relative flex items-center gap-1 px-2 py-1.5 rounded-full hover:bg-[#1e3aaa] transition-colors"
                >
                  {(() => { const lc = lovePeriod === "1d" ? loves24 : lovePeriod === "7d" ? loves7 : loves30; return (
                    <>
                      <span data-testid="header-love-count" className="text-sm font-extrabold text-white tabular-nums">{lc}</span>
                      <Heart size={18} data-testid="header-love-heart" className="text-[#e11d48]" fill={lc >= 1 ? "#e11d48" : "none"} />
                      <span data-testid="header-love-period" className="text-[10px] font-extrabold text-white/60 leading-none">{lovePeriod === "1d" ? "1D" : lovePeriod === "7d" ? "7D" : "30D"}</span>
                    </>
                  ); })()}
                </button>
                {false && bellOpen && (
                  <div data-testid="bell-dropdown" className="absolute right-0 mt-2 w-72 sms-card p-0 z-50 overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#f7c6d2] bg-[#fdecef]">
                      <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#f43f5e,#e11d48)" }}><Heart size={14} className="text-white" fill="#fff" /></span>
                      <span className="font-extrabold text-sm text-[#171426]">Love you've received</span>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {loves.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-[#9a96ac]">
                          <Heart size={22} className="mx-auto mb-1.5 text-[#f7c6d2]" />
                          No Love yet — share your videos to collect hearts!
                        </div>
                      ) : (
                        loves.map((lv) => (
                          <button
                            key={lv.id}
                            data-testid={`bell-love-${lv.id}`}
                            onClick={() => { setBellOpen(false); if (lv.manual_id) navigate(`/community/${lv.manual_id}`); }}
                            className="w-full text-left px-4 py-2.5 flex items-start gap-2.5 hover:bg-[#fdecef] transition-colors border-b border-[#f7e3e8] last:border-b-0"
                          >
                            <span className="shrink-0 flex items-center gap-0.5 text-xs font-extrabold text-[#171426] bg-[#fdecef] rounded-full px-1.5 py-0.5 border border-[#f7c6d2]">
                              <span>{lv.from_avatar?.emoji || "🙂"}</span><span className="tabular-nums">{lv.from_avatar?.tag || ""}</span>
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-extrabold text-[#be123c] flex items-center gap-1"><Heart size={11} fill="#e11d48" className="text-[#e11d48]" /> Loved your video</div>
                              <div className="text-[10px] text-[#9a96ac] truncate">on “{lv.manual_title}”</div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative" ref={ref}>
            <button
              data-testid="header-avatar-btn"
              onClick={() => setMenu((m) => !m)}
              className="ml-0 sm:ml-1 w-9 h-9 rounded-[10px] flex items-center justify-center text-white font-bold text-sm overflow-hidden shrink-0 bg-transparent transition-transform hover:scale-110"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : user?.avatar?.emoji ? (
                <span className="text-2xl leading-none" data-testid="header-avatar-emoji">{user.avatar.emoji}</span>
              ) : (
                initial
              )}
            </button>
            {menu && (
              <div
                data-testid="account-menu"
                className="absolute right-0 mt-2 w-56 p-2 z-50 rounded-2xl border-2 border-[#5d8a52] shadow-[0_10px_40px_rgba(0,0,0,0.6),0_0_20px_rgba(93,138,82,0.25)]"
                style={{ background: "#0f0f16" }}
              >
                <div className="px-3 py-2">
                  <div className="font-semibold text-sm truncate text-white">{user?.name}</div>
                  <div className="text-xs text-white/55 truncate">{user?.email}</div>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={user?.unlimited ? { background: "#16241040", color: "#8fbf82", border: "1px solid #5d8a52" } : { background: "#1a1a22", color: "#ffffff", border: "1px solid #ffffff20" }}>
                      {user?.unlimited ? <><Crown size={12}/> Unlimited</> : <><WalletIcon size={12}/> ${(((user?.credits ?? 0)) / 100).toFixed(2)}</>}
                    </span>
                    {user?.unlimited && (
                      <button
                        data-testid="open-admin-costs-btn"
                        onClick={() => { setMenu(false); setShowCosts(true); }}
                        title="Costs & Margin (admin)"
                        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-black border border-[#5d8a52] text-white hover:bg-[#3f6238] transition-colors"
                      >
                        <Lock size={12} />
                      </button>
                    )}
                  </div>
                </div>
                {onOpenSettings && (
                  <button
                    data-testid="menu-settings-btn"
                    onClick={() => { setMenu(false); onOpenSettings(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                  >
                    <Settings size={16} className="text-[#8fbf82]" /> Settings
                  </button>
                )}
                <button
                  data-testid="menu-analytics-btn"
                  onClick={() => { setMenu(false); navigate("/analytics"); }}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  <span className="flex items-center gap-2"><BarChart3 size={16} className="text-[#8fbf82]" /> Analytics</span>
                </button>
                <button
                  data-testid="menu-seller-btn"
                  onClick={() => { setMenu(false); navigate("/seller"); }}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  <span className="flex items-center gap-2"><Store size={16} className="text-[#8fbf82]" /> Sell &amp; Earn</span>
                  {user?.seller?.onboarded && <span className="text-[10px] font-bold text-[#8fbf82] bg-[#16241040] border border-[#5d8a52] px-1.5 py-0.5 rounded-full">Active</span>}
                </button>
                <button
                  data-testid="menu-avatar-btn"
                  onClick={() => { setMenu(false); setShowAvatar(true); }}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  <span className="flex items-center gap-2"><Smile size={16} className="text-[#8fbf82]" /> Avatar</span>
                  {user?.avatar?.emoji && (
                    <span className="text-xs font-bold text-[#8fbf82]">{user.avatar.emoji}{user.avatar.tag}</span>
                  )}
                </button>
                <button
                  data-testid="menu-create-btn"
                  onClick={() => { setMenu(false); navigate("/studio"); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-extrabold text-white hover:bg-white/10 transition-colors"
                >
                  <Clapperboard size={16} className="text-[#8fbf82]" /> Create
                </button>
                {diyOn && (
                  <button
                    data-testid="menu-diy-btn"
                    onClick={() => { setMenu(false); navigate("/diy"); }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                  >
                    <Hammer size={16} className="text-[#8fbf82]" /> Video Creator Mode
                  </button>
                )}
                <button
                  data-testid="menu-faq-btn"
                  onClick={() => { setMenu(false); navigate("/faq"); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  <HelpCircle size={16} className="text-[#8fbf82]" /> FAQ
                </button>
                <button
                  data-testid="menu-contact-btn"
                  onClick={() => { setMenu(false); setShowContact(true); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  <Mail size={16} className="text-[#8fbf82]" /> Contact
                </button>
                <button
                  data-testid="logout-btn"
                  onClick={() => { logout(); navigate("/"); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  <LogOut size={16} className="text-[#8fbf82]" /> Log out
                </button>
              </div>
            )}
              </div>
            </>
          )}
        </div>
      </div>
      {showAvatar && <AvatarModal onClose={() => setShowAvatar(false)} />}
      <AdminCostsModal open={showCosts} onClose={() => setShowCosts(false)} />
      <ContactModal open={showContact} onClose={() => setShowContact(false)} user={user} />
    </header>
  );
}
